import { type ReactNode,useEffect, useRef, useState } from "react";

export function InfoPopover({ text, label = "More info" }: { text: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        className="rounded p-0.5 text-[13px] leading-none text-text-secondary hover:bg-panel-2 hover:text-text-primary"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⓘ
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full z-30 mt-1 max-h-[min(60vh,280px)] w-[min(100vw-24px,380px)] overflow-auto rounded-lg border border-border bg-panel-2 p-2.5 text-left text-[11px] leading-snug text-text-primary shadow-xl"
          role="tooltip"
        >
          <div className="whitespace-pre-wrap">{text}</div>
        </div>
      ) : null}
    </div>
  );
}

export function ChartCard({
  title,
  criteria,
  note,
  titleRight,
  compact,
  children,
}: {
  title: string;
  /** Shown in the ⓘ popover: events, properties, and aggregation used for this chart. */
  criteria?: string;
  note?: string;
  titleRight?: ReactNode;
  /** Shorter card (no min-height) for dense charts like funnels. */
  compact?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-xl border border-border bg-panel-3 p-[10px] ${compact ? "min-h-0" : "min-h-[260px]"}`}
    >
      <div className={`flex flex-wrap items-center justify-between gap-x-2 gap-y-1 ${compact ? "mb-1" : "mb-[6px]"}`}>
        <div className="flex min-w-0 items-center gap-1">
          <div className="truncate text-sm font-bold text-text-primary">{title}</div>
          {criteria ? <InfoPopover text={criteria} label="How this chart is built" /> : null}
        </div>
        {titleRight ? (
          <div className="flex max-w-full flex-wrap items-center justify-end gap-2">{titleRight}</div>
        ) : null}
      </div>
      {children}
      {note ? (
        <div className={`text-xs text-text-secondary ${compact ? "mt-1" : "mt-2 min-h-[18px]"}`}>
          {note}
        </div>
      ) : null}
    </div>
  );
}
